import axios from "axios";
import TokenService from "../Services/authentication/token.service";
import { ENV_CONFIG } from "./envConfig";
import { refershTokenApi } from "../Services/Users.service";

const apiInstance = axios.create({
  baseURL: ENV_CONFIG.apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{ resolve: Function; reject: Function }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

const clearSessionAndRedirect = (): void => {
  TokenService.clearAllTokens();
  const logoutUrl = ENV_CONFIG.logoutUrl;
  if (logoutUrl) {
    window.location.assign(logoutUrl);
  } else {
    window.location.reload();
  }
};

const failRefreshAndLogout = (queueError: unknown): void => {
  processQueue(queueError, null);
  clearSessionAndRedirect();
};

const queueRequestDuringRefresh = (originalRequest: any) =>
  new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  }).then((token) => {
    originalRequest.headers['Authorization'] = `Bearer ${token}`;
    return apiInstance(originalRequest);
  });

const retryAfterTokenRefresh = async (originalRequest: any) => {
  const result = await refershTokenApi();
  if (result !== 'success') {
    failRefreshAndLogout(new Error('Token refresh failed'));
    return;
  }

  const newToken = TokenService.getLocalAccessToken();
  if (!newToken) {
    failRefreshAndLogout(new Error('Token refresh failed'));
    return;
  }

  processQueue(null, newToken);
  originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
  return apiInstance(originalRequest);
};

apiInstance.interceptors.request.use(
  (config: any) => {
    const token = TokenService.getLocalAccessToken();
    if (token) {
      if (!config.headers) config.headers = {};
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return queueRequestDuringRefresh(originalRequest);
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const retriedResponse = await retryAfterTokenRefresh(originalRequest);
      if (retriedResponse) {
        return retriedResponse;
      }
    } catch (refreshError: unknown) {
      console.error('Error during token refresh:', refreshError);
      failRefreshAndLogout(refreshError);
    } finally {
      isRefreshing = false;
    }

    return Promise.reject(error);
  }
);

export default apiInstance;
